
import { Injectable } from '@angular/core';
import { PlatformService } from './platform.service';
import { Device, DeviceId, DeviceInfo } from '@capacitor/device';
import { AsyncSubject, combineLatest, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { getFingerprint, setOption } from '@thumbmarkjs/thumbmarkjs'
import { CryptoService } from '../utility/crypto.service';
import { SecureStorageService, SecureStorageType } from './secure-storage.service';


@Injectable({
    providedIn: 'root'
})
export class DeviceInfoService {

	private static deviceId: string | undefined;
	private static deviceInfo: DeviceInfo;

	private static readonly DEVICE_ID_KEY = 'app_device_id';
	private static readonly DEVICE_FP_KEY = 'app_device_fp';

	constructor(private srvSecureStorage: SecureStorageService) { }

	public init(): AsyncSubject<boolean> {
		let ret = new AsyncSubject<boolean>();
		combineLatest([this.initDeviceId(), Device.getInfo()]).subscribe(([deviceId, info]) => {
			DeviceInfoService.deviceId = deviceId;
			DeviceInfoService.deviceInfo = info;
			ret.next(true);
			ret.complete();
		});
		return ret;
	}

	private initDeviceId(): AsyncSubject<string> {
    let ret = new AsyncSubject<string>();

    if (PlatformService.isBrowser()) {

			setOption('exclude', ['system.browser.version']);

			getFingerprint(false).then((fingerprint) => {

				const fp: string = typeof fingerprint === 'string' ? fingerprint : fingerprint.hash;

				const fpHash = CryptoService.sha256(fp);

				// 1. Load stored deviceId (PRIMARY SOURCE OF TRUTH)
				let storedDeviceId = localStorage.getItem(DeviceInfoService.DEVICE_ID_KEY);

				// 2. FIRST TIME ONLY → initialize from fingerprint
				if (!storedDeviceId) {
					storedDeviceId = fpHash;
					localStorage.setItem(DeviceInfoService.DEVICE_ID_KEY, storedDeviceId);
				}

				// 3. Always store latest fingerprint (for risk detection only)
				localStorage.setItem(DeviceInfoService.DEVICE_FP_KEY, fpHash);

				// 4. IMPORTANT:
				// NEVER overwrite storedDeviceId again

				ret.next(storedDeviceId);
				ret.complete();
			});

    } else if (PlatformService.isAndroid()) {
			// SSAID (ANDROID_ID) is the durable identity across reinstall for the same
			// signing key + user + device. Secure storage is only a per-install cache.
			Device.getId().then((id: DeviceId) => {
				const ssaid = id.identifier;
				this.persistNativeDeviceId(ssaid).subscribe(() => {
					ret.next(ssaid);
					ret.complete();
				});
			});

    } else {
			// iOS: Prefer localStorage, then Keychain (secure storage). Generate a new
			// GUID only when neither has one. Keychain typically survives reinstall;
			// not used: identifierForVendor.
			const fromLocal = localStorage.getItem(DeviceInfoService.DEVICE_ID_KEY);
			of(fromLocal).pipe(
				switchMap((localId) => {
					if (localId) {
						return of(localId);
					}
					return this.srvSecureStorage.get(SecureStorageType.DEVICE_ID).pipe(
						take(1),
						switchMap((stored) => of(stored || crypto.randomUUID()))
					);
				}),
				switchMap((deviceId) =>
					this.persistNativeDeviceId(deviceId).pipe(
						take(1),
						switchMap(() => of(deviceId))
					)
				)
			).subscribe((deviceId) => {
				ret.next(deviceId);
				ret.complete();
			});
    }

		return ret;
	}

	/** Cache device id in secure storage + localStorage mirror for sync getters. */
	private persistNativeDeviceId(deviceId: string): AsyncSubject<boolean> {
		let ret = new AsyncSubject<boolean>();
		localStorage.setItem(DeviceInfoService.DEVICE_ID_KEY, deviceId);
		this.srvSecureStorage.get(SecureStorageType.DEVICE_ID).pipe(take(1)).subscribe((stored) => {
			if (stored === deviceId) {
				ret.next(true);
				ret.complete();
				return;
			}
			this.srvSecureStorage.set(SecureStorageType.DEVICE_ID, deviceId).pipe(take(1)).subscribe((ok) => {
				ret.next(ok);
				ret.complete();
			});
		});
		return ret;
	}

	public static getDeviceId(): string {
    return DeviceInfoService.deviceId || localStorage.getItem(DeviceInfoService.DEVICE_ID_KEY) || 'unknown-device';
	}

	public static getDeviceInfo(): DeviceInfo {
		return DeviceInfoService.deviceInfo;
	}

	public static getDeviceModel(): string {
		return DeviceInfoService.getDeviceInfo()?.model ?? 'Unknown';
	}

	public static getDeviceBrandName(): string {
		return DeviceInfoService.getDeviceInfo()?.manufacturer ?? 'Unknown';
	}

	public static resetDeviceId(): void {
    localStorage.removeItem(DeviceInfoService.DEVICE_ID_KEY);
    localStorage.removeItem(DeviceInfoService.DEVICE_FP_KEY);
		DeviceInfoService.deviceId = undefined;
	}

}
